import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../utils/api';
import {
	editorInputClassName,
	editorLabelClassName,
} from '../pages/common/editor/editorStyles';

const EMPTY_DOCTORS = [];

export default function DoctorMultiSelect({ value = [], onChange, initialDoctors = EMPTY_DOCTORS }) {
	const [query, setQuery] = useState('');
	const [doctors, setDoctors] = useState(initialDoctors);
	const [error, setError] = useState('');
	const initialDoctorsRef = useRef(initialDoctors);
	const requestId = useRef(0);
	initialDoctorsRef.current = initialDoctors;

	const fetchDoctors = useCallback(() => {
		const currentRequestId = ++requestId.current;
		api.doctorSearch({ query, page: 1 })
			.then(result => {
				if (currentRequestId !== requestId.current) return;
				setDoctors(() => {
					const doctorId = doctor => doctor.doctor_id || doctor.doctor;
					const merged = new Map();
					(result.doctors || []).forEach(doctor => {
						if (doctorId(doctor)) merged.set(doctorId(doctor), { ...doctor, doctor_id: doctorId(doctor) });
					});
					initialDoctorsRef.current.forEach(doctor => {
						if (doctorId(doctor)) merged.set(doctorId(doctor), { ...doctor, doctor_id: doctorId(doctor) });
					});
					return [...merged.values()];
				});
				setError('');
			})
			.catch(err => {
				if (currentRequestId === requestId.current) {
					setError(err?.data?.message || 'دریافت فهرست پزشکان ناموفق بود');
				}
			});
	}, [query]);

	useEffect(() => {
		const timeout = setTimeout(fetchDoctors, 250);
		return () => clearTimeout(timeout);
	}, [fetchDoctors]);

	const selectedIds = useMemo(() => new Set(value.map(Number)), [value]);

	const doctorLabel = (doctor) =>
		doctor.name || doctor.phone_number || `پزشک ${doctor.doctor_id}`;

	const selectedDoctors = useMemo(() => {
		const byId = new Map();
		doctors.forEach(d => {
			const id = Number(d.doctor_id || d.doctor);
			if (id) byId.set(id, d);
		});
		initialDoctors.forEach(d => {
			const id = Number(d.doctor_id || d.doctor);
			if (id) byId.set(id, d);
		});
		return [...selectedIds].map(id => byId.get(id)).filter(Boolean);
	}, [doctors, initialDoctors, selectedIds]);

	const changeDoctor = (doctorId, checked) => {
		const next = new Set(value.map(Number));
		if (checked) next.add(Number(doctorId));
		else next.delete(Number(doctorId));
		onChange([...next]);
	};

	const removeDoctor = (doctorId) => changeDoctor(doctorId, false);

	return (
		<div className="rounded-[14px] border border-darkBlue/10 bg-white p-4 md:p-5">
			<label className={editorLabelClassName}>مدرس‌ها / پزشکان</label>
			<p className="mb-3 font-Vazir-400 text-[12px] text-darkBlue/50">
				با جستجو پزشک را پیدا کنید و تیک بزنید. انتخاب‌ها در بالا نمایش داده می‌شوند.
			</p>
			{selectedDoctors.length > 0 && (
				<div className="mb-3 flex flex-wrap gap-2">
					{selectedDoctors.map(doctor => (
						<span
							key={doctor.doctor_id}
							className="inline-flex items-center gap-1.5 rounded-full bg-lightCta/80 px-3 py-1 font-Vazir-500 text-[12px] text-darkBlue ring-1 ring-darkBlue/10"
						>
							{doctorLabel(doctor)}
							<button
								type="button"
								onClick={() => removeDoctor(doctor.doctor_id)}
								className="rounded-full px-1 font-Vazir-700 text-darkBlue/60 hover:text-[#cc0000]"
								aria-label={`حذف ${doctorLabel(doctor)}`}
							>
								×
							</button>
						</span>
					))}
				</div>
			)}
			<input
				value={query}
				onChange={event => setQuery(event.target.value)}
				placeholder="جستجوی نام یا شماره پزشک…"
				className={editorInputClassName}
			/>
			<div className="mt-3 max-h-44 overflow-y-auto rounded-[12px] border border-darkBlue/10 bg-[#FAFCFE] p-2">
				{doctors.filter(doctor => doctor.doctor_id).map(doctor => (
					<label
						key={doctor.doctor_id}
						className="flex cursor-pointer items-center gap-3 rounded-[10px] px-2 py-2 font-Vazir-400 text-[14px] text-darkBlue transition hover:bg-white"
					>
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-darkBlue/30 text-cta focus:ring-cta/30"
							checked={selectedIds.has(Number(doctor.doctor_id))}
							onChange={event => changeDoctor(doctor.doctor_id, event.target.checked)}
						/>
						{doctorLabel(doctor)}
					</label>
				))}
				{doctors.length === 0 && (
					<p className="py-4 text-center font-Vazir-400 text-[13px] text-darkBlue/45">
						پزشکی یافت نشد.
					</p>
				)}
			</div>
			{error && <p className="mt-2 font-Vazir-400 text-[13px] text-[#cc0000]">{error}</p>}
		</div>
	);
}
